import type {
  EventHandlerCb,
  ExtendedConstraints,
  CapabilityConfig,
} from '../../types';
import { hasOnlyKeysFromSet } from '../../utils';

export const id = 'file-service';

export enum Events {
  UploadLinksResult = 'file-service:upload-links-result',
  FileMetadataResult = 'file-service:file-metadata-result',
  DeleteResult = 'file-service:delete-result',
  ServiceUrlResult = 'file-service:service-url-result',
  UploadStatus = 'file-service:upload-status',
}

export enum Actions {
  RequestUploadLinks = 'file-service:request-upload-links',
  GetFileMetadata = 'file-service:get-file-metadata',
  DeleteFiles = 'file-service:delete-files',
  GetServiceUrl = 'file-service:get-service-url',
}

export type FileDescriptor =
  | {
      fileName: string;
      contentType: string;
      isPublic: true;
    }
  | {
      fileName: string;
      contentType: string;
      isPublic: false;
      ttl: number;
    };

export type RequestUploadLinksPayload = {
  clientId: string;
  files: FileDescriptor[];
};

export type UploadLinkResult =
  | {
      fileName: string;
      uploadLink: string;
      fileUuid: string;
    }
  | { fileName: string; errorMessage: string };

export type UploadLinksResultPayload = UploadLinkResult[];

export type GetFileMetadataPayload = {
  fileUuids: string[];
};

export type FileMetadataErrorCode = 'FileNotFound' | 'Unknown';

export type FileMetadataResult =
  | {
      uuid: string;
      filename?: string;
      expiresOn?: string;
      status?: 'started' | 'ready';
      downloadLink?: string;
    }
  | { uuid: string; errorMessage: string; errorCode?: FileMetadataErrorCode };

export type FileMetadataResultPayload = FileMetadataResult[];

export type DeleteFilesPayload = {
  fileUuids: string[];
};

export type DeleteFileResult =
  | { fileUuid: string; isSuccess: true }
  | { fileUuid: string; isSuccess: false; errorMessage: string };

export type DeleteResultPayload = DeleteFileResult[];

export type ServiceUrlResult =
  | {
      serviceUrl: string;
      resourcePath: string;
      region: string;
    }
  | { errorMessage: string };

export type UploadStatusPayload = {
  clientId: string;
  uuid: string;
  status: 'ready' | 'failed';
  downloadLink?: string;
};

const VALID_CONFIG_KEYS = ['type'] as const;

export type Config = CapabilityConfig & {
  type: typeof id;
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;

  eventPayloadTuple:
    | [Events.UploadLinksResult, UploadLinksResultPayload]
    | [Events.FileMetadataResult, FileMetadataResultPayload]
    | [Events.DeleteResult, DeleteResultPayload]
    | [Events.ServiceUrlResult, ServiceUrlResult]
    | [Events.UploadStatus, UploadStatusPayload];

  eventHandlerTuple:
    | [Events.UploadLinksResult, EventHandlerCb<UploadLinksResultPayload>]
    | [Events.FileMetadataResult, EventHandlerCb<FileMetadataResultPayload>]
    | [Events.DeleteResult, EventHandlerCb<DeleteResultPayload>]
    | [Events.ServiceUrlResult, EventHandlerCb<ServiceUrlResult>]
    | [Events.UploadStatus, EventHandlerCb<UploadStatusPayload>];

  actionPayloadTuple:
    | [Actions.RequestUploadLinks, RequestUploadLinksPayload]
    | [Actions.GetFileMetadata, GetFileMetadataPayload]
    | [Actions.DeleteFiles, DeleteFilesPayload]
    | [Actions.GetServiceUrl];
}>;

export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  return true;
}
